import { useState, useEffect } from 'react'
import axios from 'axios'
import { X, Clock, TrendingUp, AlertCircle, CheckCircle2, PauseCircle, XCircle, Loader2 } from 'lucide-react'

const API_BASE = import.meta.env.VITE_RAG_API_URL || 'http://localhost:8000'

interface RolloutDetail {
    id: string
    policy_name: string
    status: string
    current_impact_rate: number
    target_impact_rate: number
    current_stage_index: number
    last_transition_at: string | null
    transition_reason: string | null
    pause_reason: string | null
    rollback_reason: string | null
    freeze_reason: string | null
    started_at: string | null
    completed_at: string | null
    created_at: string
}

interface RolloutDetailPanelProps {
    rolloutId: string
    onClose: () => void
}

export function RolloutDetailPanel({ rolloutId, onClose }: RolloutDetailPanelProps) {
    const [rollout, setRollout] = useState<RolloutDetail | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        fetchRolloutDetail()
    }, [rolloutId])

    const fetchRolloutDetail = async () => {
        setIsLoading(true)
        setError(null)
        try {
            const { data } = await axios.get(`${API_BASE}/rollout/executions/${rolloutId}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                }
            })
            setRollout(data)
        } catch (err: any) {
            console.warn('Failed to fetch rollout detail:', err.message)
            setError('Falha ao carregar detalhes do rollout')
        } finally {
            setIsLoading(false)
        }
    }

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'RUNNING': return <CheckCircle2 className="w-5 h-5 text-green-400" />
            case 'PAUSED_BY_RISK': return <PauseCircle className="w-5 h-5 text-orange-400" />
            case 'ROLLED_BACK': return <XCircle className="w-5 h-5 text-red-400" />
            case 'COMPLETED': return <CheckCircle2 className="w-5 h-5 text-blue-400" />
            case 'FROZEN': return <AlertCircle className="w-5 h-5 text-purple-400" />
            default: return <Clock className="w-5 h-5 text-gray-400" />
        }
    }

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'RUNNING': return 'text-green-400 bg-green-500/10 border-green-500/20'
            case 'PAUSED_BY_RISK': return 'text-orange-400 bg-orange-500/10 border-orange-500/20'
            case 'ROLLED_BACK': return 'text-red-400 bg-red-500/10 border-red-500/20'
            case 'COMPLETED': return 'text-blue-400 bg-blue-500/10 border-blue-500/20'
            case 'FROZEN': return 'text-purple-400 bg-purple-500/10 border-purple-500/20'
            default: return 'text-gray-400 bg-gray-500/10 border-gray-500/20'
        }
    }

    const getTransitionReason = () => {
        if (!rollout) return null
        return rollout.pause_reason || rollout.rollback_reason || rollout.freeze_reason || rollout.transition_reason || 'N/A'
    }

    const formatDuration = (start: string | null, end: string | null) => {
        if (!start) return 'N/A'
        const startDate = new Date(start)
        const endDate = end ? new Date(end) : new Date()
        const diffMs = endDate.getTime() - startDate.getTime()
        const diffMins = Math.floor(diffMs / 60000)
        const hours = Math.floor(diffMins / 60)
        const mins = diffMins % 60
        return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`
    }

    const formatTimestamp = (timestamp: string | null) => {
        if (!timestamp) return 'N/A'
        const date = new Date(timestamp)
        return date.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        })
    }

    if (isLoading) {
        return (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
                <div className="bg-gray-900 border border-white/10 rounded-2xl p-8 max-w-2xl w-full mx-4">
                    <div className="flex items-center justify-center gap-3 text-white">
                        <Loader2 className="w-6 h-6 animate-spin" />
                        <span>Carregando detalhes...</span>
                    </div>
                </div>
            </div>
        )
    }

    if (error || !rollout) {
        return (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
                <div className="bg-gray-900 border border-white/10 rounded-2xl p-8 max-w-2xl w-full mx-4">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-bold text-red-400">Erro</h2>
                        <button onClick={onClose} className="text-gray-400 hover:text-white">
                            <X className="w-6 h-6" />
                        </button>
                    </div>
                    <p className="text-gray-300">{error || 'Rollout não encontrado'}</p>
                </div>
            </div>
        )
    }

    const progressPercentage = (rollout.current_impact_rate / rollout.target_impact_rate) * 100

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 border border-white/10 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="p-6 border-b border-white/10 flex justify-between items-start">
                    <div>
                        <h2 className="text-2xl font-bold text-white font-mono">{rollout.policy_name}</h2>
                        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg border mt-2 ${getStatusColor(rollout.status)}`}>
                            {getStatusIcon(rollout.status)}
                            <span className="font-medium">{rollout.status}</span>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-auto p-6 space-y-6">
                    {/* Progress Overview */}
                    <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                        <div className="flex items-center gap-2 mb-4">
                            <TrendingUp className="w-5 h-5 text-blue-400" />
                            <h3 className="text-lg font-semibold text-white">Progress Overview</h3>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-4">
                            <div>
                                <span className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Estágio Atual</span>
                                <span className="text-2xl font-bold text-white font-mono">
                                    {rollout.current_stage_index > 0 ? `${rollout.current_stage_index}` : 'N/A'}
                                </span>
                            </div>
                            <div>
                                <span className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Impact Rate</span>
                                <span className="text-2xl font-bold text-white font-mono">
                                    {(rollout.current_impact_rate * 100).toFixed(0)}%
                                </span>
                                <span className="text-sm text-gray-400 ml-1">
                                    / {(rollout.target_impact_rate * 100).toFixed(0)}%
                                </span>
                            </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="relative h-3 bg-black/40 rounded-full overflow-hidden">
                            <div
                                className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-500"
                                style={{ width: `${Math.min(progressPercentage, 100)}%` }}
                            ></div>
                        </div>
                        <div className="flex justify-between text-xs text-gray-500 mt-2 font-mono">
                            <span>0%</span>
                            <span>{progressPercentage.toFixed(1)}%</span>
                            <span>Target: {(rollout.target_impact_rate * 100).toFixed(0)}%</span>
                        </div>
                    </div>

                    {/* Last Transition */}
                    <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                        <div className="flex items-center gap-2 mb-4">
                            <AlertCircle className="w-5 h-5 text-orange-400" />
                            <h3 className="text-lg font-semibold text-white">Última Transição</h3>
                        </div>

                        <div className="space-y-3">
                            <div>
                                <span className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Timestamp</span>
                                <span className="text-white font-mono">{formatTimestamp(rollout.last_transition_at)}</span>
                            </div>
                            <div>
                                <span className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Estado</span>
                                <span className="text-white font-medium">{rollout.status}</span>
                            </div>
                            <div>
                                <span className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Razão</span>
                                <span className="text-white">{getTransitionReason()}</span>
                            </div>
                        </div>
                    </div>

                    {/* Timeline */}
                    <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                        <div className="flex items-center gap-2 mb-4">
                            <Clock className="w-5 h-5 text-purple-400" />
                            <h3 className="text-lg font-semibold text-white">Timeline</h3>
                        </div>

                        <div className="space-y-3">
                            <div className="flex justify-between">
                                <span className="text-gray-400">Criado</span>
                                <span className="text-white font-mono">{formatTimestamp(rollout.created_at)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-400">Iniciado</span>
                                <span className="text-white font-mono">{formatTimestamp(rollout.started_at)}</span>
                            </div>
                            {rollout.completed_at && (
                                <div className="flex justify-between">
                                    <span className="text-gray-400">Completado</span>
                                    <span className="text-white font-mono">{formatTimestamp(rollout.completed_at)}</span>
                                </div>
                            )}
                            <div className="flex justify-between pt-2 border-t border-white/10">
                                <span className="text-gray-400 font-medium">Duração</span>
                                <span className="text-white font-mono font-bold">
                                    {formatDuration(rollout.started_at, rollout.completed_at)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
